import { UseFormReturn } from "react-hook-form";
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  Input,
  Combobox,
} from "@altimateai/lego";
import { Spinner, Stack } from "@altimateai/lego";
import { UserGroupEntities, UserGroupRuleTypes, AccessRulesFormValues } from "./types";
import AsyncEntitySelector from "@/components/AsyncEntitySelector";

interface AccessRuleValueFieldProps {
  form: UseFormReturn<AccessRulesFormValues>;
  index: number;
  entityType: UserGroupEntities;
  matchType: UserGroupRuleTypes;
  datastoreOptions: { label: string; value: string }[];
  dbtIntegrationOptions: { label: string; value: string }[];
  isDbtIntegrationsLoading: boolean;
}

const AccessRuleValueField = ({
  form,
  index,
  entityType,
  matchType,
  datastoreOptions,
  dbtIntegrationOptions,
  isDbtIntegrationsLoading,
}: AccessRuleValueFieldProps) => {
  const shouldShowDropdown =
    (matchType === UserGroupRuleTypes.EXACT || matchType === UserGroupRuleTypes.IN) &&
    (entityType === UserGroupEntities.USER ||
      entityType === UserGroupEntities.WAREHOUSE ||
      entityType === UserGroupEntities.DATABASE ||
      entityType === UserGroupEntities.SCHEMA ||
      entityType === UserGroupEntities.ROLE ||
      entityType === UserGroupEntities.ACCOUNT ||
      entityType === UserGroupEntities.WORKSPACE);

  const isMultiSelect = matchType === UserGroupRuleTypes.IN;

  switch (entityType) {
    case UserGroupEntities.QUERY_TAG: {
      return (
        <>
          <FormField
            control={form.control}
            name={`rules.${index}.key`}
            render={({ field }) => (
              <FormItem>
                <FormLabel className={"al-mb-2"}>Key</FormLabel>
                <FormControl>
                  <Input placeholder="Enter Key" {...field} className="!al-m-0" />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <div className="al-mt-2">
            <FormField
              control={form.control}
              name={`rules.${index}.entity`}
              render={({ field }) => (
                <FormItem>
                  <FormLabel className={"al-mb-2"}>Value</FormLabel>
                  <FormControl>
                    <Input placeholder="Enter value" {...field} className="!al-m-0" />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
        </>
      );
    }
    case UserGroupEntities.DATASTORE: {
      return (
        <FormField
          control={form.control}
          name={`rules.${index}.entity`}
          render={({ field }) => (
            <FormItem>
              <FormLabel>Instance</FormLabel>
              <FormControl>
                <Combobox
                  options={datastoreOptions}
                  value={field.value || ""}
                  onChange={field.onChange}
                  placeholder="Select instance..."
                  multiSelect={false}
                  buttonProps={{
                    className: "al-w-full",
                    "aria-label": "Select instance",
                  }}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
      );
    }
    case UserGroupEntities.DBT_INTEGRATION: {
      return (
        <FormField
          control={form.control}
          name={`rules.${index}.entity`}
          render={({ field }) => (
            <FormItem>
              <FormLabel>Integration Name</FormLabel>
              {isDbtIntegrationsLoading ? (
                <Stack gap={"md"} align={"center"}>
                  <Spinner size={"sm"} />
                  <span className={"al-text-muted-foreground"}>Loading dbt integrations</span>
                </Stack>
              ) : (
                <FormControl>
                  <Combobox
                    options={dbtIntegrationOptions}
                    value={field.value || ""}
                    onChange={field.onChange}
                    placeholder="Select integration..."
                    multiSelect={false}
                    buttonProps={{
                      className: "al-w-full",
                      "aria-label": "Select integration",
                    }}
                  />
                </FormControl>
              )}

              <FormMessage />
            </FormItem>
          )}
        />
      );
    }
    default: {
      if (shouldShowDropdown) {
        return (
          <AsyncEntitySelector
            entityType={entityType}
            staticOptions={[]}
            isMultiSelect={isMultiSelect}
            form={form}
            fieldName={`rules.${index}.entity`}
            label="Entity"
          />
        );
      }
      return (
        <FormField
          control={form.control}
          name={`rules.${index}.entity`}
          render={({ field }) => (
            <FormItem>
              <FormLabel>Entity</FormLabel>
              <FormControl>
                <Input placeholder="Enter entity value" {...field} className="!al-m-0" />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
      );
    }
  }
};

export default AccessRuleValueField;
